import { Box, Tab, Tabs } from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import usePrevious from "~/hooks/usePrevProps";
import { useTabNavLocation } from "~/hooks/useTabNavLocation";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { InsertTestEventDto } from "~/redux/warehouse/testingTools";
import {
  usePostInternalTestEventMutation,
  useStartPtlTestMutation
} from "~/redux/warehouse/testingTools.hook";

import { WorkstationSummaryDto } from "~/types/api";

import BinToPortTest from "./binToPort/BinToPortTest";
import PTLTestWorkflow from "./ptl/PTLTestWorkflow";
import ScannerTest from "./scanner/ScannerTest";
import TestingToolsLogs from "./testingLogs/TestingToolsLogs";
import {
  addEvent,
  resetTestingToolsState,
  selectActivePtlPhaseDetails,
  testingToolList
} from "./testingTools.slice";

import { TestingTool } from "./testingTools.types";
import { getEventTypeFromTestType } from "./testingToolsUtils";

const resetInteractionFlags = () => {
  return testingToolList.reduce<Record<TestingTool, boolean>>(
    (acc, t) => ({ ...acc, [t]: false }),
    {} as Record<TestingTool, boolean>
  );
};

const TestingToolsView = () => {
  const { errorToast } = useToast();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const validTabs = [
    "scanner-test",
    "binToPort-test",
    "ptl-test",
    "testing-logs"
  ];
  const { tabIndex, handleTabIndexChange } = useTabNavLocation({
    defaultTab: "scanner-test",
    validTabs,
    key: "testing-tools-tab"
  });

  const selectedWs = useAppSelector(selectThisWorkstation);
  const activePTLTestPhase = useAppSelector(selectActivePtlPhaseDetails);

  // Tracks whether the user’s first interaction happened for each TestingTool
  const initialTestInteractionRef = useRef<Record<TestingTool, boolean>>(
    resetInteractionFlags()
  );
  const shoulResetPTLIds = useRef(false);
  const prevSelectedWs = usePrevious(selectedWs);

  const [startPtlTest] = useStartPtlTestMutation();
  const [addTestEvent] = usePostInternalTestEventMutation();

  const clearTestingToolsState = useCallback(
    async (
      selectedWs: WorkstationSummaryDto | null,
      displaysToClean?: number[]
    ) => {
      if (!selectedWs) return;

      if (!displaysToClean) {
        dispatch(resetTestingToolsState());
        return;
      }
      try {
        // If selectedWs and displaysToClean are present, clean displays
        await startPtlTest({
          displaysToActivate: [],
          displaysToClean,
          gridId: selectedWs.autostoreGridId,
          workstationId: selectedWs.id
        }).unwrap();
      } catch (error) {
        errorToast(getMessageFromRtkError(error));
      } finally {
        dispatch(resetTestingToolsState());
      }
    },
    [startPtlTest, dispatch, errorToast]
  );

  useEffect(() => {
    // only run when we truly switch WS (skip first mount or same WS)
    if (
      !selectedWs || // no WS -> nothing to do
      !prevSelectedWs || // first mount -> skip
      prevSelectedWs.id === selectedWs.id // same WS -> skip
    ) {
      return;
    }

    // Reset the interaction flags and testing tools state for the new WS
    initialTestInteractionRef.current = resetInteractionFlags();
    dispatch(resetTestingToolsState());
  }, [selectedWs, prevSelectedWs, dispatch]);

  useEffect(() => {
    const isPTLPingTestActiveOrComplete =
      activePTLTestPhase.phase === "ping_verification" &&
      activePTLTestPhase.status !== "preview" &&
      !!activePTLTestPhase.deviceStatusById;

    shoulResetPTLIds.current = isPTLPingTestActiveOrComplete;
  }, [activePTLTestPhase]);

  useEffect(() => {
    return () => {
      const displaysToClean = shoulResetPTLIds.current
        ? selectedWs?.totePlacements.map((tp) => tp.putToLightModuleId)
        : undefined;

      void clearTestingToolsState(selectedWs, displaysToClean);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTestingSessionStart = useCallback(
    async (testType: TestingTool) => {
      if (!selectedWs) return;

      if (initialTestInteractionRef.current[testType]) return;

      // flip the test flag early to prevent double logging
      initialTestInteractionRef.current[testType] = true;
      // log local event immediately regardless of server event success
      dispatch(
        addEvent({
          text: `${testType} test initiated`,
          state: "Info",
          testOrigin: testType
        })
      );

      try {
        const eventData: InsertTestEventDto = {
          eventTimestamp: new Date(),
          eventType: getEventTypeFromTestType(testType),
          workstationId: selectedWs.id,
          data: { deviceId: selectedWs.deviceId }
        };
        await addTestEvent({ eventData }).unwrap();
      } catch (err) {
        errorToast(
          `Unable to log ${testType} test initiation remotely: ${getMessageFromRtkError(
            err
          )})`
        );
      }
    },
    [addTestEvent, dispatch, errorToast, selectedWs]
  );

  return (
    <>
      <Tabs
        value={tabIndex}
        onChange={(_event, newValue) => {
          handleTabIndexChange(newValue as string);
        }}
        sx={{
          ml: 2
        }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label={t("scanner test")} value="scanner-test" />
        <Tab label={t("bin to port test")} value="binToPort-test" />
        <Tab label={t("pick to light test")} value="ptl-test" />
        <Tab label={t("testing logs")} value="testing-logs" />
      </Tabs>

      <Box
        id="tab-content"
        sx={{
          height: `calc(100% - 48px)`,
          overflow: "scroll",
          "&::-webkit-scrollbar": {
            display: "none"
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none"
        }}
      >
        {tabIndex === "scanner-test" && (
          <ScannerTest onTestingSessionStart={onTestingSessionStart} />
        )}
        {tabIndex === "binToPort-test" && (
          <BinToPortTest onTestingSessionStart={onTestingSessionStart} />
        )}
        {tabIndex === "ptl-test" && (
          <PTLTestWorkflow onTestingSessionStart={onTestingSessionStart} />
        )}
        {tabIndex === "testing-logs" && <TestingToolsLogs />}
      </Box>
    </>
  );
};

export default TestingToolsView;
